import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';
import { SelectionType } from 'guideline/domain';
import { LegacySidebarService } from 'guideline/feature-sidebar';
import { ConfirmationDialogArgs, ConfirmationDialogComponent, DialogService } from 'shared/ui-common';

import {
  FlowchartActionDto,
  FlowchartDecisionNodeDto,
  FlowchartElementType,
  FlowchartLinkDto,
  FlowchartNodeDto,
  FlowchartNodeType,
  FlowchartStartNodeDto,
  GuidelineActionSignificance,
  GuidelineComponentDto,
  GuidelineComponentType,
  ProposedComponentDto,
} from '@treetop/shared/model';

import { FlowchartSidebarStoreService } from '../../../../../../../guideline/feature-sidebar/src/lib/infrastructure/flowchart-sidebar-store.service';
import { FlowchartConfig } from '../../base/flowchart.config';
import { FlowchartElementsSubjectStore } from '../../data/flowchart-elements-subject-store.service';
import { BaseGraph } from '../../flowchart/graph/BaseGraph';
import { FlowchartModelService } from '../../flowchart/model.service';
import { BasePaper } from '../../flowchart/paper/BasePaper';
import { FlowchartViewService } from '../../flowchart/view.service';
import { FlowchartLoaderService } from '../flowchart-loader.service';
import { NodePickerEvent } from '../node-picker-toolbar/node-picker-toolbar-items.config';
import NodePickerTransactionService from '../node-picker-toolbar/node-picker-transaction.service';
import OperationsService from '../operations.service';

type ProposedComponentTransfer = {
  proposedComponent: ProposedComponentDto;
  title: string;
  description?: string | null;
  references?: FlowchartNodeDto['references'];
  links?: FlowchartNodeDto['links'];
  attachments?: FlowchartNodeDto['attachments'];
  descriptionReferences?: FlowchartNodeDto['descriptionReferences'];
  descriptionBelowReferences?: FlowchartNodeDto['descriptionBelowReferences'];
  resourceTags?: FlowchartNodeDto['resourceTags'];
  showDescription?: boolean;
  colorPresetId?: string | null;
  customStyling?: FlowchartNodeDto['customStyling'];
};

@Injectable()
export default class ElementActivitiesTransactionService {
  private dialog = inject(MatDialog);

  paperSource: BasePaper;
  graphSource: BaseGraph;
  private flowchartElementsSubjectStore = inject(FlowchartElementsSubjectStore);
  private flowchartSidebarStoreService = inject(FlowchartSidebarStoreService);
  private flowchartLoaderService = inject(FlowchartLoaderService);
  private nodePickerTransactionService = inject(NodePickerTransactionService);
  private operationsService = inject(OperationsService);
  private sidebarService = inject(LegacySidebarService);
  private flowchartViewService = inject(FlowchartViewService);
  private flowchartModelService = inject(FlowchartModelService);
  private translocoService = inject(TranslocoService);
  private dialogService = inject(DialogService);

  private flowchartId: string;

  constructor() {
    this.paperSource = this.flowchartViewService.getPaper();
    this.graphSource = this.flowchartModelService.getGraph();
  }

  public init(flowchartId: string) {
    this.flowchartId = flowchartId;
  }

  handleConfirmationDialog(event: NodePickerEvent, exchangedElement: joint.dia.CellView) {
    const exchangedElementData = this.sidebarService.deepSearchElements(
      this.flowchartElementsSubjectStore.getValue(),
      exchangedElement.model.id as string,
    );
    const dataLossItems = exchangedElementData ? this.getNonTransferableDataItemsForExchange(exchangedElementData, event) : [];
    const canTransferData =
      exchangedElementData && !!this.getProposedComponentTransferForActionDecisionExchange(exchangedElementData, event);
    const confirmationDialogArgs: ConfirmationDialogArgs = {
      message: canTransferData
        ? 'Flowchart.ConfirmationDialog.ChangeComponent.Message'
        : 'Flowchart.ConfirmationDialog.ChangeComponent.ResetMessage',
      cancelButtonCaption: 'Cancel',
      confirmButtonCaption: 'Flowchart.ConfirmationDialog.ChangeComponent.ConfirmButton',
      additionalParams: dataLossItems.length > 0 ? dataLossItems : undefined,
    };

    const confirmationDialog = this.dialogService.open(ConfirmationDialogComponent, {
      icon: 'triangle-alert',
      title: 'Flowchart.ConfirmationDialog.ChangeComponent.Title',
      data: confirmationDialogArgs,
      disableClose: true,
    });

    confirmationDialog.afterClosed().subscribe((isConfirmed: boolean) => {
      if (isConfirmed) {
        this.handleElementExchange(event, exchangedElement);
      }
    });
  }

  private getNonTransferableDataItemsForExchange(removedElementData: FlowchartNodeType, event: NodePickerEvent): string[][] {
    const dataLossItems: string[][] = [];
    const proposedTransfer = this.getProposedComponentTransferForActionDecisionExchange(removedElementData, event);

    if (!proposedTransfer) {
      return dataLossItems;
    }

    if (removedElementData.elementType === FlowchartElementType.FlowchartAction) {
      const action = (removedElementData as FlowchartActionDto).guidelineAction;

      if (action?.medicationRequests?.length > 0) {
        dataLossItems.push(this.getDataLossItem('MedicationRequests'));
      }

      if (
        action?.recurrence != null ||
        action?.recurrenceInterval ||
        action?.selectionBehaviorForChildren != null ||
        action?.selectionBehaviorForSiblings != null ||
        action?.significance != null ||
        action?.preferredOptionOrder != null ||
        action?.priority != null ||
        action?.argument
      ) {
        dataLossItems.push(this.getDataLossItem('ActionSettings'));
      }
    }

    if (removedElementData.elementType === FlowchartElementType.FlowchartDecisionNode) {
      const decision = removedElementData as FlowchartDecisionNodeDto;

      if (
        decision.rule ||
        decision.rulePrettyfied ||
        decision.ruleTitle ||
        decision.ruleDescription ||
        decision.guidelineCriteria?.findingDefinition ||
        decision.guidelineObservation?.observationDefinition ||
        decision.guidelineObservation?.unit ||
        !!decision.guidelineObservation?.codeableConceptValues?.length ||
        decision.outgoingLinks?.some((link) => !!link.attribute)
      ) {
        dataLossItems.push(this.getDataLossItem('DecisionSettings'));
      }
    }

    return dataLossItems;
  }

  private getDataLossItem(key: string): string[] {
    return [
      this.translocoService.translate(`Flowchart.ConfirmationDialog.ChangeComponent.DataLoss.${key}.Title`),
      this.translocoService.translate(`Flowchart.ConfirmationDialog.ChangeComponent.DataLoss.${key}.Description`),
    ];
  }

  handleConfirmationDialog_StartNode(exchangedElement: joint.dia.CellView) {
    const confirmationDialogArgs: ConfirmationDialogArgs = {
      message: 'Flowchart.ConfirmationDialog.ResetStartNode.Message',
      cancelButtonCaption: 'Cancel',
      confirmButtonCaption: 'Flowchart.ConfirmationDialog.ResetStartNode.ConfirmButton',
    };

    const confirmationDialog = this.dialogService.open(ConfirmationDialogComponent, {
      icon: 'triangle-alert',
      title: 'Flowchart.ConfirmationDialog.ResetStartNode.Title',
      data: confirmationDialogArgs,
      disableClose: true,
    });

    confirmationDialog.afterClosed().subscribe((isConfirmed: boolean) => {
      if (isConfirmed) {
        const exchangedElementFormData = this.flowchartElementsSubjectStore
          .getValue()
          .find((oldElement) => oldElement.id === exchangedElement.model.id);

        if (exchangedElementFormData.elementType === FlowchartElementType.FlowchartStartNode) {
          this.replaceStartNode(exchangedElement);
        }
      }
    });
  }

  handleElementExchange(event: NodePickerEvent, exchangedElement: joint.dia.CellView): void {
    const parentId: string = exchangedElement.model.attributes.subflowParentId as string;
    const removedElementData = this.sidebarService.deepSearchElements(
      this.flowchartElementsSubjectStore.getValue(),
      exchangedElement.model.id as string,
    );
    if (!removedElementData) {
      return;
    }

    const proposedComponentToTransfer = this.getProposedComponentTransferForActionDecisionExchange(removedElementData, event);
    const incomingElements = this.getIncomingElements(exchangedElement);

    // Remove all outgoing links that go as outgoing link towards
    // the removed element (form + flowchart)
    const incomingLinks = this.getIncomingLinks(incomingElements, exchangedElement);
    const initialElementLinkData = this.findLinkInGraph(incomingLinks[0]?.fromFlowchartNodeId, incomingLinks[0]?.toFlowchartNodeId);

    this.removeLinks(incomingLinks);

    // Get all outgoing links from removed element to other elements
    const outgoingLinks = removedElementData.outgoingLinks;

    // Create element from first link
    let initialElementToLink = this.paperSource.paper.findViewByModel(incomingElements[0]?.id);
    if (!initialElementToLink && exchangedElement.model.attributes.subflowParentId) {
      initialElementToLink = this.paperSource.paper.findViewByModel(
        this.graphSource.graph.getCell(exchangedElement.model.attributes.subflowParentId),
      );
    }

    // Remove element from form and graph (outgoing links will be removed automatically)
    this.graphSource.graph.removeCells([exchangedElement.model]);
    this.flowchartLoaderService.removeElement(exchangedElement.model, this.flowchartElementsSubjectStore, this.operationsService);

    //get position of replaced element
    const prevPosition = exchangedElement.model.attributes.position;

    let newElementId: string | null = null;

    if (!initialElementLinkData && !parentId && !!proposedComponentToTransfer) {
      const newElement = this.nodePickerTransactionService.createStandaloneElement(event, prevPosition);
      this.paperSource.paper.updateViews();
      newElementId = (newElement?.id as string) ?? null;
    } else if (parentId) {
      if (incomingElements?.length > 0 && initialElementLinkData) {
        newElementId = this.nodePickerTransactionService.handleNodePickerEventCreateNode(event, {
          sourceElement: initialElementToLink as joint.dia.ElementView,
          sourceConnector: initialElementLinkData.source().anchor,
          targetConnector: initialElementLinkData.target().anchor,
          subflow: true,
          subflowLinkedElement: true,
          previousPosition: prevPosition,
          skipSearchPopover: !!proposedComponentToTransfer,
          skipSetMinLinkLength: true,
        });
      } else {
        newElementId = this.nodePickerTransactionService.handleNodePickerEventCreateNode(event, {
          sourceElement: initialElementToLink as joint.dia.ElementView,
          sourceConnector: { name: 'bottom' },
          targetConnector: { name: 'top' },
          subflow: true,
          subflowLinkedElement: false,
          previousPosition: prevPosition,
          skipSearchPopover: !!proposedComponentToTransfer,
          skipSetMinLinkLength: true,
        });
      }
    } else {
      newElementId = this.nodePickerTransactionService.handleNodePickerEventCreateNode(event, {
        sourceElement: initialElementToLink as joint.dia.ElementView,
        sourceConnector: initialElementLinkData?.source()?.anchor,
        targetConnector: initialElementLinkData?.target()?.anchor,
        subflow: false,
        subflowLinkedElement: false,
        previousPosition: prevPosition,
        skipSearchPopover: !!proposedComponentToTransfer,
        skipSetMinLinkLength: true,
      });
    }

    // Add other outgoing links
    if (outgoingLinks?.length > 0) {
      this.addOutgoingLinks(outgoingLinks, parentId ? parentId : null);
    }

    if (proposedComponentToTransfer && newElementId) {
      this.transferProposedComponentToExchangedElement(event, newElementId, proposedComponentToTransfer);
    }
  }

  private getProposedComponentTransferForActionDecisionExchange(
    removedElementData: FlowchartNodeType,
    event: NodePickerEvent,
  ): ProposedComponentTransfer | null {
    const isActionToDecision =
      removedElementData.elementType === FlowchartElementType.FlowchartAction && event === NodePickerEvent.ADD_DECISION;
    const isDecisionToAction =
      removedElementData.elementType === FlowchartElementType.FlowchartDecisionNode && event === NodePickerEvent.ADD_ACTION;

    if (!isActionToDecision && !isDecisionToAction) {
      return null;
    }

    if (removedElementData.elementType === FlowchartElementType.FlowchartAction) {
      return this.createProposedComponentTransfer(removedElementData, (removedElementData as FlowchartActionDto).guidelineAction);
    }

    const decision = removedElementData as FlowchartDecisionNodeDto;
    return this.createProposedComponentTransfer(removedElementData, decision.guidelineCriteria ?? decision.guidelineObservation);
  }

  private createProposedComponentTransfer(
    removedElementData: FlowchartNodeType,
    guidelineComponent: GuidelineComponentDto | null | undefined,
  ): ProposedComponentTransfer | null {
    if (!guidelineComponent?.proposedComponent) {
      return null;
    }

    return {
      proposedComponent: guidelineComponent.proposedComponent,
      title: guidelineComponent.title ?? guidelineComponent.proposedComponent.title,
      description: guidelineComponent.description ?? guidelineComponent.proposedComponent.description,
      references: removedElementData.references ?? guidelineComponent.references,
      links: removedElementData.links ?? guidelineComponent.links,
      attachments: removedElementData.attachments ?? guidelineComponent.attachments,
      descriptionReferences: removedElementData.descriptionReferences ?? guidelineComponent.descriptionReferences,
      descriptionBelowReferences: removedElementData.descriptionBelowReferences ?? guidelineComponent.descriptionBelowReferences,
      resourceTags: removedElementData.resourceTags,
      showDescription: removedElementData.showDescription,
      colorPresetId: removedElementData.colorPresetId,
      customStyling: removedElementData.customStyling,
    };
  }

  private transferProposedComponentToExchangedElement(
    event: NodePickerEvent,
    newElementId: string,
    transfer: ProposedComponentTransfer,
  ): void {
    const flowchartElements = this.flowchartElementsSubjectStore.getCloneDeepValue();
    const newElement = this.sidebarService.deepSearchElements(flowchartElements, newElementId);

    if (!newElement) {
      return;
    }

    if (event === NodePickerEvent.ADD_ACTION && newElement.elementType === FlowchartElementType.FlowchartAction) {
      this.assignProposedComponentToAction(newElement as FlowchartActionDto, transfer);
    } else if (event === NodePickerEvent.ADD_DECISION && newElement.elementType === FlowchartElementType.FlowchartDecisionNode) {
      this.assignProposedComponentToDecision(newElement as FlowchartDecisionNodeDto, transfer);
    } else {
      return;
    }

    this.flowchartElementsSubjectStore.next(flowchartElements);
    this.setExchangedElementTitle(newElement.id, transfer.title);
    this.operationsService.addElementTags(newElement);
    if (newElement.showDescription && transfer.description) {
      this.operationsService.initElementsDescription(newElement.id, transfer.description);
    }
    this.sidebarService.updateCurrentElement(this.flowchartId, newElement.id, flowchartElements);
  }

  private createGuidelineComponentFromTransfer(
    componentType: GuidelineComponentType,
    transfer: ProposedComponentTransfer,
  ): GuidelineComponentDto {
    const id = crypto.randomUUID();

    return {
      id,
      uniqueId: id,
      componentType,
      proposedComponent: transfer.proposedComponent,
      proposedComponentCode: transfer.proposedComponent.code,
      title: transfer.title,
      description: transfer.description,
      references: transfer.references,
      links: transfer.links,
      attachments: transfer.attachments,
      descriptionReferences: transfer.descriptionReferences,
      descriptionBelowReferences: transfer.descriptionBelowReferences,
    };
  }

  private assignProposedComponentToAction(element: FlowchartActionDto, transfer: ProposedComponentTransfer): void {
    element.guidelineAction = {
      ...this.createGuidelineComponentFromTransfer(GuidelineComponentType.Action, transfer),
      significance: GuidelineActionSignificance.Required,
    };
    element.guidelineActionId = element.guidelineAction.id;
    this.assignTransferableNodeData(element, transfer);
  }

  private assignProposedComponentToDecision(element: FlowchartDecisionNodeDto, transfer: ProposedComponentTransfer): void {
    element.guidelineCriteria = this.createGuidelineComponentFromTransfer(GuidelineComponentType.Criteria, transfer);
    element.guidelineCriteriaId = element.guidelineCriteria.id;
    element.guidelineObservation = null;
    element.guidelineObservationId = null;
    this.assignTransferableNodeData(element, transfer);
  }

  private assignTransferableNodeData(element: FlowchartNodeType, transfer: ProposedComponentTransfer): void {
    element.references = transfer.references;
    element.links = transfer.links;
    element.attachments = transfer.attachments;
    element.descriptionReferences = transfer.descriptionReferences;
    element.descriptionBelowReferences = transfer.descriptionBelowReferences;
    element.resourceTags = transfer.resourceTags;
    element.showDescription = transfer.showDescription;
    element.colorPresetId = transfer.colorPresetId;
    element.customStyling = transfer.customStyling;
  }

  private setExchangedElementTitle(elementId: string, title: string): void {
    const cell = this.graphSource.graph.getCell(elementId);
    cell?.attr('label/text', title);
    this.paperSource.paper.updateViews();
  }

  replaceStartNode(startElement: joint.dia.CellView) {
    const flowchartElements = this.flowchartElementsSubjectStore.getCloneDeepValue();
    const newStartNode = flowchartElements[0] as FlowchartStartNodeDto;

    newStartNode.guidelineActionId = null;
    newStartNode.guidelineAction = null;
    newStartNode.guidelineCriteriaId = null;
    newStartNode.guidelineCriteria = null;
    newStartNode.rule = null;
    newStartNode.rulePrettyfied = null;

    newStartNode.references = [];
    newStartNode.links = [];
    newStartNode.attachments = [];

    flowchartElements[0] = newStartNode;

    const startNodeCell = this.graphSource.graph.getCell(startElement.model.id);
    startNodeCell.prop('attrs/label/text', this.translocoService.translate(FlowchartConfig.defaultTitleKey));
    this.paperSource.paper.updateViews();

    this.flowchartSidebarStoreService.setDto({ type: SelectionType.Flowchart, element: flowchartElements[0] });

    this.sidebarService.updateCurrentElement(this.flowchartId, startElement.model.id as string, flowchartElements);
  }

  removeElementFromFlowchart(element: joint.dia.Cell): void {
    this.graphSource.graph.removeCells([element]);
    this.flowchartLoaderService.removeElementFromSubject(this.flowchartElementsSubjectStore, element.id as string);
  }

  getIncomingElements(removedElement: joint.dia.CellView): FlowchartNodeDto[] {
    const getElementsWithOutgoingLinks = [];
    if (removedElement.model.attributes.subflowParentId) {
      const subflow = this.sidebarService.deepSearchElements(
        this.flowchartElementsSubjectStore.getValue(),
        removedElement.model.attributes.subflowParentId as string,
      );

      subflow.children.forEach((element) => {
        if (element?.outgoingLinks?.length > 0) {
          element.outgoingLinks.forEach((outgoingLink) => {
            if (outgoingLink.toFlowchartNodeId === removedElement.model.id) {
              getElementsWithOutgoingLinks.push(element);
            }
          });
        }
      });
    } else {
      this.flowchartElementsSubjectStore.getValue().forEach((element) => {
        if (element?.outgoingLinks?.length > 0) {
          element.outgoingLinks.forEach((outgoingLink) => {
            if (outgoingLink.toFlowchartNodeId === removedElement.model.id) {
              getElementsWithOutgoingLinks.push(element);
            }
          });
        }
      });
    }

    return getElementsWithOutgoingLinks;
  }

  getIncomingLinks(elements: FlowchartNodeDto[], removedElement: joint.dia.CellView): FlowchartLinkDto[] {
    const incomingLinks = [];

    elements.forEach((element) => {
      element.outgoingLinks.forEach((link) => {
        if (link.toFlowchartNodeId === removedElement.model.id) {
          incomingLinks.push(link);
        }
      });
    });

    return incomingLinks;
  }

  removeLinks(links: FlowchartLinkDto[]) {
    links.forEach((link) => {
      const convertedLink = this.findLinkInGraph(
        link.fromFlowchartNodeId as joint.dia.Cell.ID,
        link.toFlowchartNodeId as joint.dia.Cell.ID,
      );
      this.flowchartLoaderService.removeLinkFromSubject(this.flowchartElementsSubjectStore, convertedLink);
    });
  }

  findLinkInGraph(sourceId: joint.dia.Cell.ID, targetId: joint.dia.Cell.ID): any {
    const link = this.graphSource.graph.getLinks().find((link) => {
      return link.source().id === sourceId && link.target().id === targetId;
    });

    if (link) {
      return link;
    } else {
      return null;
    }
  }

  addOutgoingLinks(outgoingLinks, subflowId?: string): void {
    if (!subflowId) {
      const flowchartElements = this.flowchartElementsSubjectStore.getCloneDeepValue();
      const newElement = flowchartElements[flowchartElements.length - 1];
      const newElementData = outgoingLinks.map((outgoingLink) => {
        return { ...outgoingLink, fromFlowchartNodeId: newElement.id };
      });

      const newElementView = this.paperSource.paper.findViewByModel(newElement.id);

      this.flowchartLoaderService.createOutgoingLinks(
        this.flowchartElementsSubjectStore.getValue(),
        this.graphSource.graph,
        this.operationsService,
        newElementData,
      );

      newElementData.forEach((element) => {
        const newLink = this.findLinkInGraph(element.fromFlowchartNodeId, element.toFlowchartNodeId);
        this.flowchartLoaderService.addLinkToSubject(this.flowchartElementsSubjectStore, newElementView as joint.dia.ElementView, newLink);
      });
    } else {
      const flowchartElements = this.flowchartElementsSubjectStore.getCloneDeepValue();
      const subflow = flowchartElements.find((element) => element.id === subflowId);
      const newElement = subflow.children[subflow.children.length - 1];
      const newElementData = outgoingLinks.map((outgoingLink) => {
        return { ...outgoingLink, fromFlowchartNodeId: newElement.id };
      });

      const newElementView = this.paperSource.paper.findViewByModel(newElement.id);

      this.flowchartLoaderService.createOutgoingLinks(flowchartElements, this.graphSource.graph, this.operationsService, newElementData);

      newElementData.forEach((element) => {
        const newLink = this.findLinkInGraph(element.fromFlowchartNodeId, element.toFlowchartNodeId);
        this.flowchartLoaderService.addLinkToSubject(this.flowchartElementsSubjectStore, newElementView as joint.dia.ElementView, newLink);
      });
    }
  }
}
